// src/components/ui/index.jsx
import { useState } from "react";
import { formatMins } from "../../utils/helpers";

// ── DESIGN TOKENS ─────────────────────────────────────────────
export const T = {
  accent:  "#F97316", accentDk: "#EA580C",
  primary: "#0F172A", surface:  "#FFFFFF",
  surfAlt: "#F8FAFC", border:   "#E2E8F0",
  text:    "#0F172A", muted:    "#64748B",
  success: "#10B981", danger:   "#EF4444",
  info:    "#3B82F6", purple:   "#7C3AED",
  teal:    "#0EA5E9", gold:     "#F59E0B",
};

// ── AVATAR ────────────────────────────────────────────────────
export function Avatar({ name = "?", size = 40, color = T.accent, src }) {
  if (src) {
    return (
      <img src={src} alt={name} style={{
        width: size, height: size, borderRadius: "50%",
        objectFit: "cover", flexShrink: 0,
      }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `linear-gradient(135deg,${color},${color}99)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "white", fontWeight: 800, fontSize: Math.round(size * 0.38),
      flexShrink: 0, userSelect: "none",
    }}>
      {(name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

// ── BUTTON ────────────────────────────────────────────────────
const BTN_STYLES = {
  primary: { background: "linear-gradient(135deg,#F97316,#EA580C)", color: "#fff", boxShadow: "0 4px 12px rgba(249,115,22,.28)" },
  success: { background: "linear-gradient(135deg,#10B981,#059669)", color: "#fff" },
  danger:  { background: "linear-gradient(135deg,#EF4444,#DC2626)", color: "#fff" },
  ghost:   { background: "#F8FAFC", color: "#0F172A", border: "1px solid #E2E8F0" },
  purple:  { background: "linear-gradient(135deg,#7C3AED,#5B21B6)", color: "#fff" },
  info:    { background: "linear-gradient(135deg,#3B82F6,#1D4ED8)", color: "#fff" },
  teal:    { background: "linear-gradient(135deg,#0EA5E9,#0284C7)", color: "#fff" },
};

export function Button({
  children, variant = "primary", size = "md", full = false,
  disabled = false, loading = false, onClick, style = {}, type = "button",
}) {
  const pad = size === "sm" ? "7px 16px" : size === "lg" ? "14px 28px" : "10px 20px";
  const fs  = size === "sm" ? 13 : size === "lg" ? 16 : 14;
  return (
    <button type={type} disabled={disabled || loading} onClick={onClick} style={{
      padding: pad, fontSize: fs, fontWeight: 700,
      borderRadius: 10, border: "none", cursor: disabled || loading ? "not-allowed" : "pointer",
      fontFamily: "inherit", display: "inline-flex", alignItems: "center",
      justifyContent: "center", gap: 6,
      width: full ? "100%" : "auto",
      opacity: disabled ? 0.6 : 1, transition: "opacity .15s",
      ...(BTN_STYLES[variant] || BTN_STYLES.primary), ...style,
    }}>
      {loading && <Spinner size={14} color="currentColor" />}
      {children}
    </button>
  );
}

// ── INPUT ─────────────────────────────────────────────────────
export function Input({ label, error, style = {}, ...props }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 600, color: T.muted, marginBottom: 5, display: "block" }}>{label}</label>}
      <input style={{
        width: "100%", padding: "11px 14px", borderRadius: 10, boxSizing: "border-box",
        border: `1.5px solid ${error ? T.danger : T.border}`,
        fontFamily: "inherit", fontSize: 14, color: T.text,
        background: T.surface, outline: "none", ...style,
      }} {...props} />
      {error && <p style={{ fontSize: 12, color: T.danger, margin: "4px 0 0" }}>{error}</p>}
    </div>
  );
}

// ── SELECT ────────────────────────────────────────────────────
export function Select({ label, children, style = {}, ...props }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 600, color: T.muted, marginBottom: 5, display: "block" }}>{label}</label>}
      <select style={{
        width: "100%", padding: "11px 14px", borderRadius: 10,
        border: `1.5px solid ${T.border}`, fontFamily: "inherit",
        fontSize: 14, color: T.text, background: T.surface,
        outline: "none", boxSizing: "border-box", ...style,
      }} {...props}>{children}</select>
    </div>
  );
}

// ── TEXTAREA ──────────────────────────────────────────────────
export function Textarea({ label, style = {}, ...props }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 600, color: T.muted, marginBottom: 5, display: "block" }}>{label}</label>}
      <textarea style={{
        width: "100%", padding: "11px 14px", borderRadius: 10,
        border: `1.5px solid ${T.border}`, fontFamily: "inherit",
        fontSize: 14, color: T.text, background: T.surface,
        outline: "none", resize: "vertical", minHeight: 80,
        boxSizing: "border-box", ...style,
      }} {...props} />
    </div>
  );
}

// ── CARD ──────────────────────────────────────────────────────
export function Card({ children, style = {}, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: T.surface, borderRadius: 14,
      padding: "18px 20px", border: `1px solid ${T.border}`,
      boxShadow: "0 1px 3px rgba(0,0,0,.06)", marginBottom: 14,
      ...(onClick ? { cursor: "pointer" } : {}), ...style,
    }}>
      {children}
    </div>
  );
}

// ── BADGE ─────────────────────────────────────────────────────
export function Badge({ children, color = T.accent, bg = "#FFF7ED", style = {} }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: 20,
      fontSize: 12, fontWeight: 700, color, background: bg, ...style,
    }}>
      {children}
    </span>
  );
}

// ── SPINNER ───────────────────────────────────────────────────
export function Spinner({ size = 24, color = T.accent }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      border: `2px solid ${color}33`, borderTopColor: color,
      animation: "rido-spin .7s linear infinite", flexShrink: 0,
    }} />
  );
}

// ── STARS ─────────────────────────────────────────────────────
export function Stars({ rating = 0, onRate, size = 15 }) {
  const [hover, setHover] = useState(0);
  const display = hover || Math.round(rating);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      {[1,2,3,4,5].map(i => (
        <span key={i}
          onMouseEnter={() => onRate && setHover(i)}
          onMouseLeave={() => onRate && setHover(0)}
          onClick={() => onRate && onRate(i)}
          style={{ fontSize: size, color: i <= display ? T.gold : "#CBD5E1",
            cursor: onRate ? "pointer" : "default", lineHeight: 1 }}>★</span>
      ))}
      {rating > 0 && !onRate && (
        <span style={{ fontSize: size - 3, color: T.muted, marginRight: 3 }}>
          {Number(rating).toFixed(1)}
        </span>
      )}
    </span>
  );
}

// ── STAT CARD ─────────────────────────────────────────────────
export function StatCard({ label, value, icon, color = T.accent }) {
  return (
    <div style={{
      background: T.surface, borderRadius: 12,
      padding: "14px 10px", textAlign: "center",
      border: `1px solid ${T.border}`,
    }}>
      <div style={{ fontSize: 22, marginBottom: 5 }}>{icon}</div>
      <div style={{ fontSize: 19, fontWeight: 800, color, marginBottom: 2 }}>{value}</div>
      <div style={{ fontSize: 11, color: T.muted }}>{label}</div>
    </div>
  );
}

// ── EMPTY STATE ───────────────────────────────────────────────
export function EmptyState({ icon, title, sub, action, actionLabel }) {
  return (
    <div style={{ textAlign: "center", padding: "56px 24px", color: T.muted }}>
      <div style={{ fontSize: 52, marginBottom: 14 }}>{icon}</div>
      <h3 style={{ margin: "0 0 8px", color: T.text, fontSize: 18, fontWeight: 700 }}>{title}</h3>
      {sub && <p style={{ margin: "0 0 20px", fontSize: 14 }}>{sub}</p>}
      {action && <Button onClick={action}>{actionLabel}</Button>}
    </div>
  );
}

// ── LOADING SCREEN ────────────────────────────────────────────
export function LoadingScreen({ text = "جاري التحميل..." }) {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: "#F1F5F9", gap: 16,
    }}>
      <div style={{ fontSize: 48 }}>🚗</div>
      <Spinner size={36} />
      <p style={{ color: T.muted, fontSize: 14, margin: 0 }}>{text}</p>
    </div>
  );
}

// ── IMAGE UPLOAD ──────────────────────────────────────────────
export function ImageUpload({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 600, color: T.muted, marginBottom: 5, display: "block" }}>{label}</label>}
      <label style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", minHeight: 80,
        border: `2px dashed ${value ? T.success : T.border}`,
        borderRadius: 12, padding: 14, cursor: "pointer",
        background: value ? "#F0FDF4" : T.surfAlt, textAlign: "center",
        transition: "all .2s",
      }}>
        <input type="file" accept="image/*" style={{ display: "none" }}
          onChange={e => e.target.files[0] && onChange(e.target.files[0])} />
        {value ? (
          <><div style={{ fontSize: 24 }}>✅</div>
          <span style={{ fontSize: 12, color: T.success, fontWeight: 600 }}>
            {value instanceof File ? value.name : "تم الرفع"}
          </span></>
        ) : (
          <><div style={{ fontSize: 28 }}>📷</div>
          <span style={{ fontSize: 12, color: T.muted }}>اضغط لاختيار صورة</span></>
        )}
      </label>
    </div>
  );
}

// ── ROUTE DISPLAY ─────────────────────────────────────────────
export function RouteDisplay({ from, to }) {
  return (
    <div style={{ background: T.surfAlt, borderRadius: 12, padding: "11px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: T.success, display: "inline-block", flexShrink: 0 }} />
        <span style={{ fontSize: 14, fontWeight: 600 }}>{from}</span>
      </div>
      <div style={{ marginRight: 4, height: 14, borderRight: "2px dashed #CBD5E1" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 10, height: 10, borderRadius: 2, background: T.accent, display: "inline-block", flexShrink: 0 }} />
        <span style={{ fontSize: 14, fontWeight: 600 }}>{to}</span>
      </div>
    </div>
  );
}

// ── TIMER BADGE ───────────────────────────────────────────────
export function TimerBadge({ label, mins, warnAt = 15 }) {
  const urgent = mins <= warnAt;
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "5px 12px", borderRadius: 20,
      background: urgent ? "#FEE2E2" : "#F0FDF4",
      color: urgent ? T.danger : T.success,
      fontSize: 13, fontWeight: 700,
    }}>
      ⏱ {label}: {mins === 0 ? "انتهت المهلة" : formatMins(mins)}
    </div>
  );
}

// ── DIVIDER ───────────────────────────────────────────────────
export function Divider({ style = {} }) {
  return <div style={{ height: 1, background: T.border, margin: "14px 0", ...style }} />;
}

// Inject spin keyframe once
if (typeof document !== "undefined" && !document.getElementById("rido-kf")) {
  const s = document.createElement("style");
  s.id = "rido-kf";
  s.textContent = "@keyframes rido-spin{to{transform:rotate(360deg)}}";
  document.head.appendChild(s);
}
